import { useEffect, useState } from "react";
import { Gauge, TrendingUp, TrendingDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PerformanceChart } from "@/components/PerformanceChart";
import { api, errorText } from "@/lib/api";
import { MONTHS, fmtCompact } from "@/lib/format";
import { PAIRS, pick, pickKpi, fmtKpi, delta, fmtDelta } from "@/lib/perf";
import { toast } from "sonner";

const KPI_ORDER = ["revenue", "gop", "gop_margin", "ebitda", "nop", "payroll", "rooms_sold", "occupancy", "adr", "revpar"];
const SERIES = (data) => [["actual", `Actual ${data.budget_year}`], ["budget", `Budget ${data.budget_year}`], ["ly", data.ly_label], ["forecast", `Forecast ${data.budget_year}`]];

const Card = ({ k, kkey, view, month, pair, data, i }) => {
  const P = (s) => pickKpi(data, kkey, s, view, month);
  const a = P(pair.a), b = P(pair.b);
  const d = delta(kkey, a, b, k);
  const good = d && (kkey === "payroll" ? d.v <= 0 : d.v >= 0);
  const primary = P("actual") ?? P("budget");
  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-4 rise rise-${(i % 3) + 1}`} data-testid={`perf-card-${kkey}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{k.label}</p>
        {d && <span className={`inline-flex items-center gap-1 text-xs font-semibold ${good ? "text-emerald-600" : "text-red-600"}`} data-testid={`perf-delta-${kkey}`}>{good ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{fmtDelta(d)}</span>}
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1 tabular" data-testid={`perf-primary-${kkey}`}>{fmtKpi(kkey, primary, k)}</p>
      <p className="text-[10px] text-slate-400 -mt-0.5">{P("actual") !== null ? "Actual" : "Budget"} · {pair.label}</p>
      <div className="grid grid-cols-4 gap-1 mt-3 text-[11px]">
        {SERIES(data).map(([s, label]) => (
          <div key={s} className={`rounded px-1.5 py-1 ${s === pair.a || s === pair.b ? "bg-slate-100" : ""}`}>
            <p className="text-slate-400 truncate">{label.split(" ")[0]}</p>
            <p className="font-mono text-slate-800 truncate" data-testid={`perf-${kkey}-${s}`}>{fmtKpi(kkey, P(s), k)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PerformancePanel = ({ projectId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [view, setView] = useState("month");
  const [pairId, setPairId] = useState(null);
  const [chartKpi, setChartKpi] = useState("revenue");
  useEffect(() => {
    api.get(`/projects/${projectId}/performance`).then((r) => {
      setData(r.data);
      setMonth((m) => m || r.data.default_month);
      setPairId((p) => p || (r.data.last_tracked_month ? "actual_budget" : "budget_ly"));
    }).catch((e) => toast.error(errorText(e)));
  }, [projectId, refreshKey]);
  if (!data || !month || !pairId) return <div className="text-sm text-slate-500" data-testid="performance-loading">Loading performance...</div>;
  const pair = PAIRS.find((p) => p.id === pairId);
  const keys = KPI_ORDER.filter((k) => data.kpis[k]);
  const tracked = new Set(data.tracked_months);
  const periodLabel = view === "month" ? `${MONTHS[month - 1]} ${data.budget_year}` : view === "ytd" ? `YTD Jan–${MONTHS[month - 1]} ${data.budget_year}` : `Full year ${data.budget_year}`;
  return (
    <div className="space-y-5" data-testid="performance-panel">
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge size={18} className="text-emerald-700 shrink-0" />
          <div className="min-w-0"><h3 className="font-heading text-base font-semibold text-slate-900 truncate">Hotel performance — {periodLabel}</h3>
            <p className="text-[11px] text-slate-500">Sum P&amp;L · {data.last_tracked_month ? `Actual through ${MONTHS[data.last_tracked_month - 1]}` : `No ${data.budget_year} actuals uploaded yet`} · Forecast basis: {data.forecast_basis || "budget"}</p></div>
        </div>
        <div className="flex flex-wrap gap-2 lg:ml-auto">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="perf-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)} data-testid={`perf-month-${i + 1}`}>{m} {data.budget_year}{tracked.has(i + 1) ? " · actual" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="h-9 w-[130px] bg-white" data-testid="perf-view-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="month" data-testid="perf-view-month">Month</SelectItem><SelectItem value="ytd" data-testid="perf-view-ytd">Year to date</SelectItem><SelectItem value="fy" data-testid="perf-view-fy">Full year</SelectItem></SelectContent>
          </Select>
          <Select value={pairId} onValueChange={setPairId}>
            <SelectTrigger className="h-9 w-[230px] bg-white" data-testid="perf-compare-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PAIRS.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`perf-compare-${p.id}`}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-3" data-testid="perf-cards">
        {keys.map((kk, i) => <Card key={kk} kkey={kk} k={data.kpis[kk]} view={view} month={month} pair={pair} data={data} i={i} />)}
      </div>
      <div className="grid xl:grid-cols-[1fr_420px] gap-5">
        <PerformanceChart data={data} kpiKey={chartKpi} onKpi={setChartKpi} keys={keys} month={month} />
        <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto" data-testid="perf-mix">
          <h3 className="font-heading text-base font-semibold text-slate-900">Revenue mix — {periodLabel}</h3>
          <table className="w-full text-xs mt-3 tabular">
            <thead><tr className="text-slate-500 text-[10px] uppercase tracking-wider"><th className="text-left py-1">Department</th><th className="text-right">Actual</th><th className="text-right">Budget</th><th className="text-right">LY</th><th className="text-right">Fcst</th><th className="text-right">Δ {pair.label.replace("Rolling ", "").replace("Forecast", "Fcst")}</th></tr></thead>
            <tbody>
              {Object.entries(data.mix).map(([name, k]) => {
                const d = delta("revenue", pick({ ...k, avg: false }, pair.a, view, month), pick({ ...k, avg: false }, pair.b, view, month));
                return (<tr key={name} className="border-t border-slate-100" data-testid={`perf-mix-${name.replace(/\W+/g, "-").toLowerCase()}`}>
                  <td className="py-1.5 text-slate-700">{name}</td>
                  {["actual", "budget", "ly", "forecast"].map((s) => <td key={s} className="text-right font-mono text-slate-800">{fmtCompact(pick({ ...k, avg: false }, s, view, month))}</td>)}
                  <td className={`text-right font-mono font-semibold ${d ? (d.v >= 0 ? "text-emerald-600" : "text-red-600") : "text-slate-400"}`}>{fmtDelta(d)}</td>
                </tr>);
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
